import type { Project } from '@hangar/shared';
import type { BuildsSearch } from '@/router';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import type { Can } from '@/lib/permissions';
import { Card, CardContent } from '@/components/ui/card';
import { BuildsFilters } from './BuildsFilters';
import { BuildsList } from './BuildsList';
import { BuildsPageHeader } from './BuildsPageHeader';
import { activeEnvironments, allTotal } from './buildsPageModel';
import { isFiltered } from './buildsFiltersModel';
import type { BuildsPageData } from './useBuildsPageData';

interface Props {
  project: Project;
  slug: string;
  search: BuildsSearch;
  setSearch: (next: BuildsSearch) => void;
  data: BuildsPageData;
  runBuildGate: { can: Can; blockedReason?: string };
  onRunBuild: () => void;
}

/** Screen 02 once the project is known: header, filter bar, builds card and the "n shown" footer. */
export function BuildsView({ project, slug, search, setSearch, data, runBuildGate, onRunBuild }: Props) {
  const { apps, builds, queue, live, rows } = data;
  const filtered = isFiltered(search);
  return (
    <div className="flex flex-col gap-5">
      <BuildsPageHeader projectName={project.name} live={live} lanes={queue.data?.lanes ?? []} />
      <BuildsFilters search={search} onChange={setSearch} apps={apps.data} environments={activeEnvironments(project.environments)} total={allTotal(filtered, rows, Boolean(builds.hasNextPage))} />
      <Card className="enter">
        <CardContent className="border-t-0 p-0 sm:p-0">
          <BuildsList
            projectSlug={slug}
            builds={rows}
            isLoading={builds.isLoading}
            isError={builds.isError}
            error={builds.error}
            onRetry={() => void builds.refetch()}
            hasMore={Boolean(builds.hasNextPage)}
            isFetchingMore={builds.isFetchingNextPage}
            onLoadMore={() => void builds.fetchNextPage()}
            filtered={filtered}
            onRunBuild={onRunBuild}
            runBuildGate={runBuildGate}
            onReset={() => setSearch({})}
          />
        </CardContent>
      </Card>
      {rows && rows.length > 0 ? <ShownFooter count={rows.length} more={Boolean(builds.hasNextPage)} /> : null}
    </div>
  );
}

function ShownFooter({ count, more }: { count: number; more: boolean }) {
  return (
    <p className="flex items-center gap-2 font-mono text-xs text-subtle">
      <Icon name="construction" size={14} />
      <Kicker className="text-subtle">{count} shown</Kicker>
      {more ? <span>· more available</span> : null}
    </p>
  );
}
